import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useCategoryMenu } from '../../hooks/useMenu';
import Bredcom from '../Bredcrumb/Main';

function CategoryPage() {
  const { categorySlug } = useParams();
  const { categoryMenu, loading, error } = useCategoryMenu(categorySlug);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('name'); // name, price, featured

  // Filter and sort items
  const getFilteredAndSortedItems = () => {
    if (!categoryMenu?.items) return [];

    let items = [...categoryMenu.items];

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      items = items.filter(item => 
        item.name.toLowerCase().includes(query) ||
        item.description.toLowerCase().includes(query)
      );
    }

    // Apply sorting
    items.sort((a, b) => {
      switch (sortBy) {
        case 'price':
          return a.price - b.price;
        case 'featured':
          return b.is_featured - a.is_featured;
        case 'name':
        default:
          return a.name.localeCompare(b.name);
      }
    });

    return items;
  };

  const filteredItems = getFilteredAndSortedItems();

  const renderMenuItem = (item) => (
    <div key={item.id} className="menu-item-card">
      <div className="item-inner">
        {/* Item Image */}
        {item.image_url && (
          <div className="item-image">
            <img 
              src={`http://localhost:3001${item.image_url}`} 
              alt={item.name}
              className="item-img"
            />
            {item.is_featured && (
              <div className="featured-badge">
                <i className="fas fa-star"></i>
                Featured
              </div>
            )}
          </div>
        )}

        {/* Item Content */}
        <div className="item-content">
          <div className="item-header">
            <h4 className="item-name">{item.name}</h4>
            <div className="item-price">JD{item.price}</div>
          </div>

          {item.description && (
            <p className="item-description">{item.description}</p>
          )}

          {/* Item Details */}
          <div className="item-details">
            {item.preparation_time && (
              <div className="detail-item">
                <i className="fas fa-clock text-info"></i>
                <span>{item.preparation_time} min</span>
              </div>
            )}

            {item.allergens && item.allergens.length > 0 && (
              <div className="detail-item">
                <i className="fas fa-exclamation-triangle text-warning"></i>
                <span>Contains: {item.allergens.join(', ')}</span>
              </div>
            )}
          </div>

          {/* Item Status */}
          <div className="item-status">
            {!item.is_available && (
              <span className="unavailable-badge">
                <i className="fas fa-times-circle"></i>
                Currently Unavailable
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <>
        <Bredcom 
          subtitle="Loading Menu..." 
          title="Please Wait" 
          Img="/assets/images/background/banner-image-2.jpg" 
        />
        <section className="category-page">
          <div className="auto-container">
            <div className="loading-state text-center" style={{padding: '100px 0'}}>
              <div className="spinner-border text-primary" role="status">
                <span className="sr-only">Loading...</span>
              </div>
              <p style={{marginTop: '20px', fontSize: '18px'}}>Loading category menu...</p>
            </div>
          </div>
        </section>
      </>
    );
  }

  if (error || !categoryMenu) {
    return (
      <>
        <Bredcom 
          subtitle="Menu Not Found" 
          title="Category Not Available" 
          Img="/assets/images/background/banner-image-2.jpg" 
        />
        <section className="category-page">
          <div className="auto-container">
            <div className="error-state text-center" style={{padding: '100px 0'}}>
              <i className="fas fa-exclamation-triangle fa-3x text-warning mb-3"></i>
              <h3>Category Not Found</h3>
              <p>The menu category you're looking for doesn't exist or is currently unavailable.</p>
              <Link to="/menu" className="theme-btn btn-style-one" style={{marginTop: '20px'}}>
                <span className="txt">Back to Menu</span>
              </Link>
            </div>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      {/* Breadcrumb */}
      <Bredcom 
        subtitle={`${categoryMenu.category.name} Menu`}
        title={categoryMenu.category.description || `Delicious ${categoryMenu.category.name}`}
        Img="/assets/images/background/banner-image-2.jpg" 
      />

      {/* Category Menu Content */}
      <section className="category-page">
        <div className="auto-container">
          
          {/* Breadcrumb Navigation */}
          <div className="menu-breadcrumb">
            <Link to="/menu" className="breadcrumb-link">
              <i className="fas fa-arrow-left me-2"></i>
              All Categories
            </Link>
            <span className="breadcrumb-separator">/</span>
            <span className="current-category">{categoryMenu.category.name}</span>
          </div>

          {/* Category Header */}
          <div className="category-header">
            <div className="row align-items-center">
              <div className="col-lg-8">
                <h2 className="category-title">{categoryMenu.category.name}</h2>
                <p className="category-description">
                  {categoryMenu.category.description || 
                   `Discover our carefully selected ${categoryMenu.category.name.toLowerCase()} items, 
                    prepared with the finest ingredients and authentic flavors.`}
                </p>
                <div className="category-stats">
                  <span className="stat-item">
                    <i className="fas fa-utensils me-1"></i>
                    {categoryMenu.items?.length || 0} Items
                  </span>
                  <span className="stat-item">
                    <i className="fas fa-star me-1"></i>
                    {categoryMenu.items?.filter(item => item.is_featured)?.length || 0} Featured
                  </span>
                </div>
              </div>
              <div className="col-lg-4 text-end">
                <Link to="/reservation" className="theme-btn btn-style-one">
                  <span className="txt">Make Reservation</span>
                </Link>
              </div>
            </div>
          </div>

          {/* Filters and Controls */}
          <div className="menu-controls">
            <div className="row align-items-center">
              <div className="col-lg-6">
                <div className="search-box">
                  <input
                    type="text"
                    placeholder={`Search in ${categoryMenu.category.name}...`}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="search-input"
                  />
                  <i className="fas fa-search search-icon"></i>
                </div>
              </div>
              <div className="col-lg-6">
                <div className="sort-controls text-end">
                  <label className="sort-label">Sort by:</label>
                  <select 
                    value={sortBy} 
                    onChange={(e) => setSortBy(e.target.value)}
                    className="sort-select"
                  >
                    <option value="name">Name (A-Z)</option>
                    <option value="price">Price (Low to High)</option>
                    <option value="featured">Featured First</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Menu Items */}
          {filteredItems.length > 0 ? (
            <div className="menu-items-grid">
              {filteredItems.map(renderMenuItem)}
            </div>
          ) : (
            <div className="no-results">
              <div className="text-center py-5">
                <i className="fas fa-search fa-3x text-muted mb-3"></i>
                <h4>No items found</h4>
                <p className="text-muted">
                  {searchQuery 
                    ? `No items match "${searchQuery}" in this category.`
                    : 'This category doesn\'t have any items yet.'
                  }
                </p>
                {searchQuery && (
                  <button 
                    className="btn btn-outline-primary"
                    onClick={() => setSearchQuery('')}
                  >
                    Clear Search
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Related Categories */}
          <div className="related-categories">
            <h3>Explore Other Categories</h3>
            <div className="category-links">
              <Link to="/menu" className="category-link-btn">
                <i className="fas fa-th-large me-2"></i>
                View All Categories
              </Link>
              <Link to="/menu/breakfast" className="category-link-btn">
                Breakfast
              </Link>
              <Link to="/menu/main-course" className="category-link-btn">
                Main Course
              </Link>
              <Link to="/menu/pizza" className="category-link-btn">
                Pizza
              </Link>
              <Link to="/menu/coffee-tea" className="category-link-btn">
                Coffee & Tea
              </Link>
            </div>
          </div>

        </div>
      </section>

      <style jsx>{`
        .category-page {
          padding: 60px 0 100px;
          background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
        }

        .menu-breadcrumb {
          display: flex;
          align-items: center;
          margin-bottom: 40px;
          font-size: 1rem;
        }

        .breadcrumb-link {
          color: #c9302c;
          text-decoration: none;
          font-weight: 500;
          transition: color 0.3s ease;
        }

        .breadcrumb-link:hover {
          color: #a02622;
        }

        .breadcrumb-separator {
          margin: 0 15px;
          color: #6c757d;
        }

        .current-category {
          color: #2c3e50;
          font-weight: 600;
        }

        .category-header {
          background: white;
          padding: 40px;
          border-radius: 20px;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
          margin-bottom: 40px;
        }

        .category-title {
          color: #2c3e50;
          font-size: 2.5rem;
          font-weight: 700;
          margin-bottom: 1rem;
        }

        .category-description {
          color: #6c757d;
          font-size: 1.1rem;
          line-height: 1.6;
          margin-bottom: 1.5rem;
        }

        .category-stats {
          display: flex;
          gap: 25px;
        }

        .stat-item {
          color: #495057;
          font-weight: 500;
          font-size: 0.95rem;
        }

        .menu-controls {
          background: white;
          padding: 25px;
          border-radius: 15px;
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
          margin-bottom: 40px;
        }

        .search-box {
          position: relative;
        }

        .search-input {
          width: 100%;
          padding: 12px 45px 12px 15px;
          border: 2px solid #e9ecef;
          border-radius: 25px;
          font-size: 1rem;
          transition: border-color 0.3s ease;
        }

        .search-input:focus {
          outline: none;
          border-color: #c9302c;
          box-shadow: 0 0 0 0.2rem rgba(201, 48, 44, 0.25);
        }

        .search-icon {
          position: absolute;
          right: 15px;
          top: 50%;
          transform: translateY(-50%);
          color: #6c757d;
        }

        .sort-controls {
          display: flex;
          align-items: center;
          gap: 15px;
        }

        .sort-label {
          color: #495057;
          font-weight: 500;
          margin: 0;
        }

        .sort-select {
          padding: 8px 15px;
          border: 2px solid #e9ecef;
          border-radius: 8px;
          background: white;
          font-size: 0.95rem;
          min-width: 180px;
        }

        .menu-items-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
          gap: 30px;
          margin-bottom: 60px;
        }

        .menu-item-card {
          background: white;
          border-radius: 20px;
          overflow: hidden;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
          transition: all 0.4s ease;
        }

        .menu-item-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 15px 40px rgba(0, 0, 0, 0.15);
        }

        .item-image {
          position: relative;
          height: 200px;
          overflow: hidden;
        }

        .item-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.4s ease;
        }

        .menu-item-card:hover .item-img {
          transform: scale(1.05);
        }

        .featured-badge {
          position: absolute;
          top: 15px;
          right: 15px;
          background: linear-gradient(45deg, #ffc107, #ff8c00);
          color: white;
          padding: 6px 12px;
          border-radius: 20px;
          font-size: 0.8rem;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 5px;
        }

        .item-content {
          padding: 25px;
        }

        .item-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 1rem;
        }

        .item-name {
          color: #2c3e50;
          font-size: 1.3rem;
          font-weight: 600;
          margin: 0;
          line-height: 1.3;
        }

        .item-price {
          color: #c9302c;
          font-size: 1.4rem;
          font-weight: 700;
        }

        .item-description {
          color: #6c757d;
          font-size: 0.95rem;
          line-height: 1.5;
          margin-bottom: 1rem;
        }

        .item-details {
          margin-bottom: 1rem;
        }

        .detail-item {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 0.5rem;
          font-size: 0.9rem;
          color: #495057;
        }

        .unavailable-badge {
          color: #dc3545;
          font-weight: 600;
          font-size: 0.9rem;
          display: flex;
          align-items: center;
          gap: 5px;
        }

        .no-results {
          background: white;
          border-radius: 20px;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
          margin-bottom: 60px;
        }

        .related-categories {
          background: white;
          padding: 40px;
          border-radius: 20px;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
          text-align: center;
        }

        .related-categories h3 {
          color: #2c3e50;
          font-size: 1.5rem;
          margin-bottom: 1.5rem;
        }

        .category-links {
          display: flex;
          justify-content: center;
          gap: 15px;
          flex-wrap: wrap;
        }

        .category-link-btn {
          padding: 10px 20px;
          background: #f8f9fa;
          color: #495057;
          text-decoration: none;
          border-radius: 25px;
          font-weight: 500;
          transition: all 0.3s ease;
        }

        .category-link-btn:hover {
          background: #c9302c;
          color: white;
          transform: translateY(-2px);
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .category-title {
            font-size: 2rem;
          }

          .menu-controls .row > div {
            margin-bottom: 15px;
          }

          .sort-controls {
            justify-content: flex-start;
          }

          .menu-items-grid {
            grid-template-columns: 1fr;
            gap: 20px;
          }

          .category-links {
            flex-direction: column;
            align-items: center;
          }

          .category-link-btn {
            width: 100%;
            max-width: 250px;
          }
        }

        @media (max-width: 480px) {
          .category-header {
            padding: 25px;
          }

          .item-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 10px;
          }

          .item-content {
            padding: 20px;
          }
        }
      `}</style>
    </>
  );
}

export default CategoryPage;